/* ---------------------------------------------------------------------
 * product.js
 * Idempotent IIFE that hydrates every `sections/product.liquid` instance
 * on the page. Responsibilities:
 *   - Variant picker (pills + native select) → resolve matching variant
 *     from inline variants JSON, update form hidden id, dispatch
 *     `product:variant-change` CustomEvent.
 *   - Price snippet listens for `product:variant-change` and re-renders.
 *   - Affirm snippet listens and updates `data-amount` / calls
 *     `affirm.ui.refresh()` if Affirm SDK is loaded.
 *   - Mulberry snippet listens and updates `window.mulberryShop.selectedVariantId`.
 *   - Skio web component fires its own `variantChanged` event; we bridge
 *     both directions so the rest of the section stays in sync.
 *   - Gallery thumbnail click → swap active slide (desktop).
 *   - Modal open/close for Flex (HSA/FSA) and Rx (prescription) blocks.
 *   - Quantity sync between standalone `quantity_selector` and inline
 *     form quantity input.
 *
 * Re-runs on Shopify section reload (theme-editor shopify:section:load)
 * via the event listener at the bottom of the file. Marks each section
 * with `data-product-hydrated` so we don't double-bind.
 * --------------------------------------------------------------------- */
(function () {
  'use strict';

  function hydrate(root) {
    if (!root || root.getAttribute('data-product-hydrated') === 'true') return;
    root.setAttribute('data-product-hydrated', 'true');

    var sectionId = root.getAttribute('data-product-section');
    if (!sectionId) return;

    var variantsScript = root.querySelector('script[data-product-variants="' + sectionId + '"]');
    var variants = [];
    if (variantsScript) {
      try {
        variants = JSON.parse(variantsScript.textContent);
      } catch {
        variants = [];
      }
    }

    var picker = root.querySelector('[data-product-variant-picker]');
    var form = root.querySelector('[data-product-form][data-section-id="' + sectionId + '"]');
    var variantInput = form ? form.querySelector('[data-product-variant-input]') : null;
    var submitButton = form ? form.querySelector('[data-product-form-submit]') : null;
    var ctaLabelEl = form ? form.querySelector('[data-product-cta-label]') : null;
    var defaultCtaLabel = ctaLabelEl ? ctaLabelEl.textContent.trim() : 'Add to Cart';

    /* ---------- Variant resolution ---------- */

    function getSelectedOptions() {
      var selected = [];
      if (!picker) return selected;

      picker.querySelectorAll('.product__variant-option').forEach(function (optEl) {
        var pos = parseInt(optEl.getAttribute('data-option-position'), 10);
        if (!pos) return;

        var pill = optEl.querySelector('.product__variant-pill[aria-checked="true"]');
        if (pill) {
          selected[pos - 1] = pill.getAttribute('data-option-value');
          return;
        }
        var select = optEl.querySelector('select[data-product-variant-select]');
        if (select) {
          selected[pos - 1] = select.value;
        }
      });
      return selected;
    }

    function resolveVariant(selectedOptions) {
      if (!selectedOptions || !selectedOptions.length) return null;
      for (var i = 0; i < variants.length; i++) {
        var v = variants[i];
        var match = true;
        for (var j = 0; j < selectedOptions.length; j++) {
          if (v.options[j] !== selectedOptions[j]) {
            match = false;
            break;
          }
        }
        if (match) return v;
      }
      return null;
    }

    function applyVariant(variant, source) {
      if (!variant) return;

      if (variantInput) variantInput.value = variant.id;

      if (submitButton) submitButton.disabled = !variant.available;
      if (ctaLabelEl) {
        ctaLabelEl.textContent = variant.available ? defaultCtaLabel : 'Sold Out';
      }

      /* Update history without reloading so back-button works */
      if (variant.url && window.history && window.history.replaceState) {
        var url = new URL(variant.url, window.location.origin);
        var newUrl = window.location.pathname + (url.search || '');
        if (variant.id) {
          var params = new URLSearchParams(window.location.search);
          params.set('variant', variant.id);
          newUrl = window.location.pathname + '?' + params.toString();
        }
        window.history.replaceState({}, '', newUrl);
      }

      document.dispatchEvent(new CustomEvent('product:variant-change', {
        detail: { sectionId: sectionId, variant: variant, source: source || 'picker' }
      }));
    }

    /* ---------- Picker bindings ---------- */

    if (picker) {
      picker.addEventListener('click', function (e) {
        var pill = e.target.closest('.product__variant-pill');
        if (!pill) return;

        var optEl = pill.closest('.product__variant-option');
        if (!optEl) return;

        optEl.querySelectorAll('.product__variant-pill').forEach(function (p) {
          p.setAttribute('aria-checked', 'false');
          p.setAttribute('aria-pressed', 'false');
        });
        pill.setAttribute('aria-checked', 'true');
        pill.setAttribute('aria-pressed', 'true');

        var variant = resolveVariant(getSelectedOptions());
        if (variant) applyVariant(variant, 'pill');
      });

      picker.addEventListener('change', function (e) {
        if (!e.target.matches('select[data-product-variant-select]')) return;
        var variant = resolveVariant(getSelectedOptions());
        if (variant) applyVariant(variant, 'select');
      });
    }

    /* ---------- Add-on funnel entry: keep variant in sync ----------
       Progressive enhancement (A/B plan Phase 1 §6). The entry anchor already
       carries the server-resolved default variant, so navigation works with JS
       off. When the shopper changes variant, keep the anchor's `variant=` param
       in sync so the funnel opens on the chosen variant. Harmless no-op on
       single-variant products. */
    var addonEntries = root.querySelectorAll('[data-addon-flow-entry]');
    if (addonEntries.length) {
      document.addEventListener('product:variant-change', function (e) {
        if (!e.detail || e.detail.sectionId !== sectionId) return;
        var v = e.detail.variant;
        if (!v || !v.id) return;
        addonEntries.forEach(function (a) {
          var href = a.getAttribute('href');
          if (!href) return;
          try {
            var u = new URL(href, window.location.origin);
            u.searchParams.set('variant', v.id);
            a.setAttribute('href', u.pathname + u.search + u.hash);
          } catch { /* noop */ }
        });
      });
    }

    /* ---------- Price snippet listener ---------- */

    var priceEl = root.querySelector('[data-product-price][data-section-id="' + sectionId + '"]');
    if (priceEl) {
      var currentEl = priceEl.querySelector('[data-product-price-current]');
      var compareEl = priceEl.querySelector('[data-product-price-compare]');
      var tagEl = priceEl.querySelector('[data-product-price-tag]');

      var formatMoney = function (cents) {
        var n = Number(cents) / 100;
        if (Number.isNaN(n)) return '';
        var rounded = Math.round(n * 100) / 100;
        var hasCents = rounded % 1 !== 0;
        return '$' + (hasCents ? rounded.toFixed(2) : rounded.toFixed(0));
      };

      /* Shopify Collective: a fixed per-vendor % discount applied to every
         variant. When present, the discounted price is primary, the
         variant's own price is the strikethrough, and the tag is "X% Off". */
      var collectiveDiscount = parseFloat(priceEl.getAttribute('data-collective-discount')) || 0;

      document.addEventListener('product:variant-change', function (e) {
        if (!e.detail || e.detail.sectionId !== sectionId) return;
        var v = e.detail.variant;
        if (!v) return;

        if (collectiveDiscount > 0) {
          var discounted = Math.round(v.price * (100 - collectiveDiscount) / 100);
          if (currentEl) currentEl.textContent = formatMoney(discounted);
          if (compareEl) {
            compareEl.textContent = formatMoney(v.price);
            compareEl.hidden = false;
          }
          if (tagEl) {
            tagEl.textContent = collectiveDiscount + '% Off';
            tagEl.hidden = false;
          }
          return;
        }

        if (currentEl) currentEl.textContent = formatMoney(v.price);

        var onSale = v.compare_at_price && v.compare_at_price > v.price;
        var savings = onSale ? v.compare_at_price - v.price : 0;
        var pct = onSale ? Math.round((savings * 100) / v.compare_at_price) : 0;

        if (compareEl) {
          if (onSale) {
            compareEl.textContent = formatMoney(v.compare_at_price);
            compareEl.hidden = false;
          } else {
            compareEl.textContent = '';
            compareEl.hidden = true;
          }
        }
        if (tagEl) {
          if (onSale) {
            var tpl = tagEl.getAttribute('data-tag-template') || '[percent]% Off';
            tagEl.textContent = tpl
              .replace('[percent]', String(pct))
              .replace('[savings]', formatMoney(savings));
            tagEl.hidden = false;
          } else {
            tagEl.textContent = '';
            tagEl.hidden = true;
          }
        }
      });
    }

    /* ---------- Affirm listener ---------- */

    var affirmEl = root.querySelector('[data-product-affirm][data-section-id="' + sectionId + '"]');
    if (affirmEl) {
      var affirmMin = parseInt(affirmEl.getAttribute('data-affirm-min'), 10) || 0;
      document.addEventListener('product:variant-change', function (e) {
        if (!e.detail || e.detail.sectionId !== sectionId) return;
        var v = e.detail.variant;
        if (!v) return;
        /* Affirm does not finance below the configured minimum — hide the
           splash for those variants and skip the refresh. */
        if (affirmMin && Number(v.price) < affirmMin) {
          affirmEl.hidden = true;
          return;
        }
        affirmEl.hidden = false;
        affirmEl.setAttribute('data-amount', String(v.price));
        if (window.affirm && window.affirm.ui && typeof window.affirm.ui.refresh === 'function') {
          window.affirm.ui.refresh();
        }
      });
    }

    /* ---------- Mulberry listener ---------- */

    var mulberryEl = root.querySelector('[data-product-mulberry][data-section-id="' + sectionId + '"]');
    if (mulberryEl) {
      document.addEventListener('product:variant-change', function (e) {
        if (!e.detail || e.detail.sectionId !== sectionId) return;
        var v = e.detail.variant;
        if (!v) return;
        mulberryEl.setAttribute('data-variant-id', String(v.id));
        if (window.mulberryShop) {
          window.mulberryShop.selectedVariantId = v.id;
        }
      });
    }

    /* ---------- Skio bridge ---------- */

    var skioEl = root.querySelector('[data-product-skio][data-section-id="' + sectionId + '"]');
    if (skioEl) {
      /* Skio fires `variantChanged` on the document when its selection changes */
      document.addEventListener('variantChanged', function (e) {
        if (!e.detail || !e.detail.variant) return;
        var v = e.detail.variant;
        var match = variants.find(function (x) { return x.id === v.id; });
        if (!match) return;
        if (variantInput) variantInput.value = match.id;
        document.dispatchEvent(new CustomEvent('product:variant-change', {
          detail: { sectionId: sectionId, variant: match, source: 'skio' }
        }));
      });

      /* Forward our picker's variant change INTO the Skio component so its
         frequency dropdown and plan availability update per variant (e.g.
         resupply bundles whose plans differ by variant). Skio's own
         `variantChanged` listener resolves the full variant — with its
         selling_plan_allocations — from its internal product data by id.
         Skip events that originated from Skio to avoid an echo loop. */
      document.addEventListener('product:variant-change', function (e) {
        if (!e.detail || e.detail.sectionId !== sectionId) return;
        if (e.detail.source === 'skio') return;
        var v = e.detail.variant;
        if (!v) return;
        document.dispatchEvent(new CustomEvent('variantChanged', {
          detail: { variantId: v.id }
        }));
      });
    }

    /* ---------- Gallery ---------- */

    var galleryRoot = root.querySelector('[data-product-gallery]');
    if (galleryRoot) {
      var slides = Array.prototype.slice.call(
        galleryRoot.querySelectorAll('[data-product-gallery-slide]')
      );
      var thumbs = Array.prototype.slice.call(
        galleryRoot.querySelectorAll('[data-product-gallery-thumb]')
      );
      var prevBtn = galleryRoot.querySelector('[data-product-gallery-prev]');
      var nextBtn = galleryRoot.querySelector('[data-product-gallery-next]');
      var mainEl = galleryRoot.querySelector('[data-product-gallery-main]');
      var activeIdx = 0;

      var isDesktop = function () {
        return window.matchMedia('(min-width: 990px)').matches;
      };

      var goTo = function (idx) {
        if (idx < 0) idx = slides.length - 1;
        if (idx >= slides.length) idx = 0;
        activeIdx = idx;

        slides.forEach(function (s, i) {
          var active = i === idx;
          if (active) {
            s.classList.add('product__gallery-slide--active');
            s.removeAttribute('aria-hidden');
          } else {
            s.classList.remove('product__gallery-slide--active');
            s.setAttribute('aria-hidden', 'true');
          }
        });
        thumbs.forEach(function (t, i) {
          var active = i === idx;
          if (active) {
            t.classList.add('product__gallery-thumb--active');
            t.setAttribute('aria-current', 'true');
          } else {
            t.classList.remove('product__gallery-thumb--active');
            t.removeAttribute('aria-current');
          }
        });

        /* On mobile we let native scroll handle the visible slide; sync
           thumbnail state by scrolling the main element to the target */
        if (!isDesktop() && mainEl && slides[idx]) {
          var left = slides[idx].offsetLeft;
          mainEl.scrollTo({ left: left, behavior: 'smooth' });
        }
      };

      thumbs.forEach(function (thumb) {
        thumb.addEventListener('click', function () {
          var idx = parseInt(thumb.getAttribute('data-product-gallery-thumb'), 10);
          goTo(idx);
        });
      });

      if (prevBtn) prevBtn.addEventListener('click', function () { goTo(activeIdx - 1); });
      if (nextBtn) nextBtn.addEventListener('click', function () { goTo(activeIdx + 1); });

      /* Mobile: update thumb state as the user scrolls the main element */
      if (mainEl) {
        var scrollTimer = null;
        mainEl.addEventListener('scroll', function () {
          if (isDesktop()) return;
          if (scrollTimer) clearTimeout(scrollTimer);
          scrollTimer = setTimeout(function () {
            var scrollLeft = mainEl.scrollLeft;
            var closest = 0;
            var closestDelta = Infinity;
            slides.forEach(function (s, i) {
              var delta = Math.abs(s.offsetLeft - scrollLeft);
              if (delta < closestDelta) {
                closestDelta = delta;
                closest = i;
              }
            });
            if (closest !== activeIdx) {
              activeIdx = closest;
              thumbs.forEach(function (t, i) {
                t.classList.toggle('product__gallery-thumb--active', i === activeIdx);
              });
            }
          }, 80);
        }, { passive: true });
      }
    }

    /* ---------- Modals (Flex + Rx) ---------- */

    root.querySelectorAll('[data-modal-target]').forEach(function (trigger) {
      trigger.addEventListener('click', function () {
        var id = trigger.getAttribute('data-modal-target');
        if (!id) return;
        var dlg = root.querySelector('#' + CSS.escape(id));
        if (!dlg) return;
        if (typeof dlg.showModal === 'function') {
          dlg.showModal();
        } else {
          dlg.setAttribute('open', '');
        }
      });
    });

    root.querySelectorAll('[data-product-modal-close]').forEach(function (closeBtn) {
      closeBtn.addEventListener('click', function () {
        var dlg = closeBtn.closest('dialog');
        if (!dlg) return;
        if (typeof dlg.close === 'function') {
          dlg.close();
        } else {
          dlg.removeAttribute('open');
        }
      });
    });

    /* Backdrop click closes — only when the click target is the dialog itself */
    root.querySelectorAll('dialog.product__modal').forEach(function (dlg) {
      dlg.addEventListener('click', function (e) {
        if (e.target === dlg) {
          if (typeof dlg.close === 'function') dlg.close();
          else dlg.removeAttribute('open');
        }
      });
    });

    /* ---------- Quantity picker (pill) + input sync ----------
     * The standalone `quantity_selector` block renders a pill with
     * [−] [<input>] [+]. The +/− buttons increment/decrement the
     * input's value, clamped to min/max attributes. The input itself
     * uses HTML5 `form="product-form-{id}"` so it submits as form data
     * even though it sits outside the <form>.
     *
     * If the form happens to have an inline qty input (legacy/custom),
     * this block keeps both inputs in sync — last-written wins on submit. */

    var qtyControl = root.querySelector('[data-product-qty]');
    var qtyStandalone = root.querySelector('[data-product-qty-input]');
    var qtyDecrement = root.querySelector('[data-product-qty-decrement]');
    var qtyIncrement = root.querySelector('[data-product-qty-increment]');
    var qtyInForm = form ? form.querySelector('input[name="quantity"]') : null;

    function clampQty(val) {
      var min = qtyStandalone ? parseInt(qtyStandalone.getAttribute('min'), 10) || 1 : 1;
      var maxAttr = qtyStandalone ? qtyStandalone.getAttribute('max') : null;
      var max = maxAttr ? parseInt(maxAttr, 10) : Infinity;
      var n = parseInt(val, 10);
      if (isNaN(n)) n = min;
      if (n < min) n = min;
      if (n > max) n = max;
      return n;
    }

    function syncQty(val) {
      var n = clampQty(val);
      if (qtyStandalone && qtyStandalone.value !== String(n)) qtyStandalone.value = n;
      if (qtyInForm && qtyInForm !== qtyStandalone && qtyInForm.value !== String(n)) {
        qtyInForm.value = n;
      }
      /* Disable decrement at the floor for clearer UX */
      if (qtyDecrement) {
        var min = qtyStandalone ? parseInt(qtyStandalone.getAttribute('min'), 10) || 1 : 1;
        qtyDecrement.disabled = n <= min;
      }
    }

    if (qtyControl && qtyStandalone) {
      syncQty(qtyStandalone.value);

      if (qtyDecrement) {
        qtyDecrement.addEventListener('click', function () {
          syncQty(parseInt(qtyStandalone.value, 10) - 1);
        });
      }
      if (qtyIncrement) {
        qtyIncrement.addEventListener('click', function () {
          syncQty(parseInt(qtyStandalone.value, 10) + 1);
        });
      }
      qtyStandalone.addEventListener('input', function () {
        syncQty(qtyStandalone.value);
      });
      qtyStandalone.addEventListener('blur', function () {
        syncQty(qtyStandalone.value);
      });
    }

    /* Form-only sync (no standalone pill): keep both inputs aligned
       when only the form-internal qty exists. */
    if (!qtyControl && qtyInForm) {
      qtyInForm.addEventListener('input', function () {
        var n = parseInt(qtyInForm.value, 10);
        if (isNaN(n) || n < 1) qtyInForm.value = '1';
      });
    }

    /* ----------------------------------------------------------------
     * Okendo stars → review widget scroll.
     * The count link (product-okendo-stars.liquid) hrefs the theme's own
     * okendo_widget block (#okendo-reviews-widget) as a no-JS fallback.
     * But reviews are usually supplied by the Okendo *app block*, a
     * separate section whose id Shopify generates dynamically
     * (shopify-block-…__okendo_reviews_widget_…) — so the fragment never
     * resolves. Locate whichever widget is actually on the page via its
     * stable data attributes and smooth-scroll to it.
     * -------------------------------------------------------------- */
    /* Combined height of the chrome pinned to the top of the viewport (the
       sticky site header + the PDP add-to-cart bar, when each is present) plus
       a small gap, so a jump target lands just below it instead of behind it.
       The add-to-cart bar hides via opacity/transform (stays display:flex), so
       its height is measurable even before it slides into view. */
    function stickyChromeOffset() {
      var offset = 20;
      var header = document.querySelector('.section-header, sticky-header, .header, header');
      if (header) {
        var pos = window.getComputedStyle(header).position;
        if (pos === 'fixed' || pos === 'sticky') offset += header.offsetHeight;
      }
      var atc = document.querySelector('.sticky-add-to-cart--pdp');
      if (atc && window.getComputedStyle(atc).display !== 'none') offset += atc.offsetHeight;
      return offset;
    }

    var okendoCounts = root.querySelectorAll('[data-product-okendo-count]');
    okendoCounts.forEach(function (countLink) {
      countLink.addEventListener('click', function (e) {
        var productId = countLink.getAttribute('data-okendo-product-id');

        /* The full reviews widget is one of these containers: the theme's own
           `product-okendo-widget` (#okendo-reviews-widget /
           data-oke-reviews-widget) or the Okendo *app block* (data-oke-widget).
           Star-rating summaries use data-oke-star-rating and are deliberately
           not matched here. */
        var widgets = [].slice.call(
          document.querySelectorAll('[data-oke-widget], [data-oke-reviews-widget]')
        );
        var scoped = productId
          ? widgets.filter(function (w) {
              return w.getAttribute('data-oke-reviews-product-id') === productId;
            })
          : [];
        var pool = scoped.length ? scoped : widgets;

        /* Prefer a widget that has actually rendered (non-zero height). When
           the Okendo app block supplies the reviews, the theme's own
           #okendo-reviews-widget stays an empty 0-height placeholder — jumping
           to it lands mid-page, which is the bug this guards against. */
        var target = null;
        for (var i = 0; i < pool.length; i++) {
          if (pool[i].offsetHeight > 0) {
            target = pool[i];
            break;
          }
        }
        target = target || pool[0] || document.querySelector('#okendo-reviews-widget');
        if (!target) return; /* fall through to native href fragment */

        e.preventDefault();
        /* Scroll the app-block / section wrapper to just below the sticky
           chrome so the widget's heading isn't hidden behind the header or the
           PDP add-to-cart bar. */
        var scrollTo = target.closest('.shopify-block, .shopify-section') || target;
        var top =
          scrollTo.getBoundingClientRect().top + window.scrollY - stickyChromeOffset();
        window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
      });
    });
  }

  function hydrateAll() {
    document.querySelectorAll('[data-product-section]').forEach(hydrate);
  }

  /* Expose hydrate so dynamically-injected product markup (e.g. the
     quick-view modal, which fetches and injects a `sections/quick-view-
     product.liquid` render) can bind the same variant / price / form /
     gallery behavior. Idempotent: `data-product-hydrated` guards re-binds. */
  window.SDProduct = window.SDProduct || {};
  window.SDProduct.hydrate = hydrate;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', hydrateAll);
  } else {
    hydrateAll();
  }

  /* Theme editor: re-hydrate on section reload */
  document.addEventListener('shopify:section:load', function (e) {
    var section = e.target && e.target.querySelector
      ? e.target.querySelector('[data-product-section]')
      : null;
    if (section) hydrate(section);
  });
})();
